import React, { useMemo, useEffect, useState } from "react";
import { Button } from "@mui/material";
import dayjs from "dayjs";
import { useApiClientQuery } from "../../../hooks/useApiClient";
import api_endpoints from "../../../api";
import PredefinedPeriods from "../../../widgets/CustomDatePicker/PredefinedPeriod";
import { currencyFormatter, formatDateTime } from "../../../utils";
import CampaignsSummaryTable from "./CampaignsSummaryTable";
import ModeHeader from "./ModeHeader";
import TrendChart from "./TrendChart";
import RightStatsPanel from "./RightStatsPanel";
import { useCustomTranslation } from "../../../hooks/useCustomTranslation";

// tiny helpers to safely turn “anything” into an array
const asArray = (v) => {
  if (Array.isArray(v)) return v;
  if (v && Array.isArray(v.data)) return v.data;           // axios-style
  if (v && Array.isArray(v.transactions)) return v.transactions;
  return [];
};

const OrganizationMode = ({
  organizationId,
  period,
  setPeriod,
  onSelectCampaign,
  onAddPromise,
  refreshKey,
}) => {
  const { t } = useCustomTranslation("Promises List");

  const selectedOrganizationName =
    localStorage.getItem("selectedOrganisationName") || "";

  const handlePeriodChange = (e) =>
    setPeriod((p) => ({ ...p, selectedPeriod: e.target.value }));

  const setpersonalizedDates = (startDate, endDate, starttime, endtime) =>
    setPeriod((p) => ({
      ...p,
      start_date: startDate,
      end_date: endDate,
      start_time: starttime.format("HH:mm"),
      end_time: endtime.format("HH:mm"),
    }));

  const { data: rawData, isFetching } = useApiClientQuery(
    () =>
      api_endpoints.getPromises(
        organizationId,
        null, // campaign_name
        null, // donor_name
        null, // email
        null, // phone
        null, // promise_amount
        null, // status
        null, // payment_type
        null, // promise_type
        null, // given_amount
        formatDateTime(period.start_date, period.start_time),
        formatDateTime(period.end_date, period.end_time)
      ),
    { queryKey: ["org_promises", organizationId, period, refreshKey] }
  );

  // Always treat API list as an array
  const data = useMemo(() => asArray(rawData), [rawData]);

  // aggregate for campaigns table and stats
  const { rows, statsRaw } = useMemo(() => {
    const by = new Map();
    data.forEach((p) => {
      const id = p.campaign_data?._id || "unknown";
      const name = p.campaign_data?.name || t("unknown_campaign");
      const bucket = by.get(id) || { id, name, count: 0, promised: 0, given: 0 };
      bucket.count += 1;
      bucket.promised += Number(p.promise_amount || 0);
      bucket.given += Number(p.given_amount || 0);
      by.set(id, bucket);
    });

    let totalPromised = 0;
    let totalGiven = 0;

    const rows = Array.from(by.values()).map((b) => {
      totalPromised += b.promised;
      totalGiven += b.given;
      return {
        id: b.id,
        campaign_id: b.id,
        campaign_name: b.name,
        count: b.count,
        promises_total: currencyFormatter(b.promised.toFixed(2)),
        promises_total_value: b.promised,
        donations_received: currencyFormatter(b.given.toFixed(2)),
        donations_received_value: b.given,
        uncollected: currencyFormatter(Math.max(0, b.promised - b.given).toFixed(2)),
        uncollected_value: Math.max(0, b.promised - b.given),
      };
    });

    return {
      rows,
      statsRaw: {
        campaignsWithFundraiser: rows.length,
        promised: totalPromised,
        given: totalGiven,
        uncollected: Math.max(0, totalPromised - totalGiven),
      },
    };
  }, [data, t]);

  const rightStats = [
    { label: t("campaigns_with_fundraiser"), value: statsRaw.campaignsWithFundraiser ?? 0 },
    { label: t("promises"), value: currencyFormatter(statsRaw.promised.toFixed(2) ?? 0) },
    { label: t("donations_received"), value: currencyFormatter(statsRaw.given.toFixed(2) ?? 0) },
    { label: t("uncollected_promises"), value: currencyFormatter(statsRaw.uncollected.toFixed(2) ?? 0) },
  ];

  // chart data (cumulative promised vs actual by day) – fully defensive
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const build = async () => {
      const list = asArray(data);
      if (!list.length) { setChartData([]); return; }

      // Promised per created_at day
      const promisedByDay = {};
      list.forEach((p) => {
        const d = p?.created_at ? dayjs(p.created_at).format("YYYY-MM-DD") : null;
        if (!d) return;
        promisedByDay[d] = (promisedByDay[d] || 0) + Number(p.promise_amount || 0);
      });

      // Actual by donation/transfer date
      const actualByDay = {};

      const txResponses = await Promise.all(
        list.map((p) =>
          api_endpoints.getCampaignPromiseTransactions(p._id, null, null, null, null)
        )
      );

      // txResponses is an array, but each element can be array|null|object
      txResponses.forEach((txListRaw) => {
        const txList = asArray(txListRaw);
        txList.forEach((tx) => {
          const raw =
            tx?.paymentDetails?.date_of_donation ||
            tx?.paymentDetails?.date_of_transfer;
          if (!raw) return;
          const d = dayjs(raw).format("YYYY-MM-DD");
          actualByDay[d] = (actualByDay[d] || 0) + Number(tx.amount || 0);
        });
      });

      // Expand daily range & accumulate
      const start = dayjs(period.start_date).startOf("day");
      const end = dayjs(period.end_date).startOf("day");
      let accP = 0, accA = 0;
      const points = [];
      for (let cur = start; !cur.isAfter(end); cur = cur.add(1, "day")) {
        const key = cur.format("YYYY-MM-DD");
        accP += promisedByDay[key] || 0;
        accA += actualByDay[key] || 0;
        points.push({ date: key, promised: accP, actual: accA });
      }

      if (!cancelled) setChartData(points);
    };

    build();
    return () => { cancelled = true; };
  }, [data, period.start_date, period.end_date]);

  return (
    <>
      <ModeHeader
        title={t("fundraisers_for_org", { organization: selectedOrganizationName })}
        right={<Button variant="contained" onClick={onAddPromise}>{t("add_promise_button")}</Button>}
      />

      <div className="container-white">
        <div className="inline-flex w-fit items-center">
        <PredefinedPeriods
          selectedPeriod={period.selectedPeriod}
          handlePeriodChange={handlePeriodChange}
          setSelectedPeriod={(v) => setPeriod((p) => ({ ...p, selectedPeriod: v }))}
          setpersonalizedDates={setpersonalizedDates}
        />
        </div>
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <TrendChart
            data={chartData}
            labels={{ promised: t("promised"), actual: t("actual_donations") }}
          />
        </div>
        <RightStatsPanel title="" stats={rightStats} />
      </div>

      <CampaignsSummaryTable rows={rows} loading={isFetching} onSelect={onSelectCampaign} />
    </>
  );
};

export default OrganizationMode;
